import { Pagy } from 'types';

// Discriminator values emitted on each Change Log row by the backend
// Audit::*Serializer subclasses (see app/serializers/audit/**/*.rb in
// bidops-app). Used in `record.kind` and as the literal types on the
// per-kind record interfaces — adding a new kind: define the literal here,
// add the interface, and extend the ChangeLogRecord union.
export const CHANGE_LOG_KIND = {
  IMPORT: 'import',
  LINE_ITEM_DELETE: 'line_item_delete',
  LINE_ITEM_UPDATE: 'line_item_update'
} as const;

export type ChangeLogSource =
  | 'automatic'
  | 'intelligent_counter_offer'
  | 'manual_entry'
  | 'replacement'
  | 'spreadsheet_import';

// One half of a before/after change tuple — backend may emit either a numeric
// or a string-encoded decimal (BigDecimal), and null for a transition from a
// missing/cleared value.
export type ChangeValuePart = number | string | null;

export type ChangeTuple = [ChangeValuePart, ChangeValuePart];

// Fields shared by every Change Log row regardless of kind. Each concrete
// record interface extends this and pins its own `kind` literal + adds
// kind-specific fields.
interface BaseChangeLogRecord {
  bid_request_name: string;
  created_at: string;
  currency: string;
  id: number;
  source: ChangeLogSource;
  whodunnit: string;
}

// Fields shared by line-item-scoped Change Log rows (update + delete). These
// rows point at a specific LineItem and carry its display info; import rows
// don't.
interface BaseLineItemChangeLogRecord extends BaseChangeLogRecord {
  display_number: string;
  line_item_id: number;
  line_item_name: string;
  line_item_sno: number;
  lot_name: string;
}

export interface ImportRecord extends BaseChangeLogRecord {
  kind: typeof CHANGE_LOG_KIND.IMPORT;
  line_item_count: number;
  source: 'spreadsheet_import';
  total_baseline: number | string;
  total_negotiation_starting_price: number | string;
}

export interface LineItemDeleteRecord extends BaseLineItemChangeLogRecord {
  kind: typeof CHANGE_LOG_KIND.LINE_ITEM_DELETE;
  negotiation_starting_price: number | string | null;
  // Mutually exclusive, and only populated while the override model flag is on: the backend
  // captures the buyer's override when one was set, otherwise the model's price.
  predictive_unit_price: number | string | null;
  price: number | string | null;
  suggested_price_override: number | string | null;
}

export interface LineItemUpdateRecord extends BaseLineItemChangeLogRecord {
  changes: Record<string, ChangeTuple>;
  kind: typeof CHANGE_LOG_KIND.LINE_ITEM_UPDATE;
}

export type ChangeLogRecord = ImportRecord | LineItemDeleteRecord | LineItemUpdateRecord;

// Union of all valid field names across any Change Log row. `keyof
// ChangeLogRecord` alone would give only the INTERSECTION (fields present
// on every row kind), which excludes per-kind columns like `no` / `value`.
// Used by `columns.tsx` to constrain `dataIndex` / `key` to real field names
// — renaming a field on any record surfaces a compile error in the columns.
export type ChangeLogColumnField =
  | keyof ImportRecord
  | keyof LineItemDeleteRecord
  | keyof LineItemUpdateRecord;

export interface LineItemHistoriesResponse {
  pagy: Pagy;
  records: ChangeLogRecord[];
}
